import { useId, useState, type FormEvent } from 'react';
import { ApiError, Button, Input, formatPct } from '@dataroom/shared';
import type { RoomSellersResponse } from '@dataroom/shared';
import { useAddSeller, useRoomSellers } from '@/api/hooks';
import { parseOwnershipPct } from '@/lib/ownership';
import { Modal } from './Modal';
import { OwnershipPctField } from './OwnershipPctField';

/**
 * Add an owner to the room — in the new-room set-up flow (an optional last step)
 * and from the Owners card on the Access page. A room can have several owners, each with an
 * ownership percentage; the owners together decide letters of intent by an
 * ownership-weighted majority. The first owner defaults to 100%; for any other
 * the admin must choose (the total may not exceed 100%). Adds through the sellers API (find-or-create in this
 * tenant + the seller-team default access). Skippable in one click.
 */
export function InviteSellerDialog({
  roomId,
  onClose,
  setup = true,
}: {
  roomId: string;
  onClose: () => void;
  /** True in the new-room set-up flow ("optional last step"); false from Settings. */
  setup?: boolean;
}) {
  const add = useAddSeller(roomId);
  const sellers = useRoomSellers(roomId);
  const [email, setEmail] = useState('');
  // null = untouched: 100 for the room's first seller, blank otherwise.
  const [pctText, setPctText] = useState<string | null>(null);
  const [added, setAdded] = useState<{
    email: string;
    pct: number | null;
    result: RoomSellersResponse | null;
  } | null>(null);
  const emailId = useId();
  const hintId = useId();

  const firstSeller = sellers.data !== undefined && sellers.data.sellers.length === 0;
  const pctValue = pctText ?? (firstSeller ? '100' : '');
  const parsed = parseOwnershipPct(pctValue);

  const trimmed = email.trim();
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  const canSubmit = validEmail && parsed.pct !== null && !add.isPending;
  const error = add.error
    ? add.error instanceof ApiError
      ? add.error.message
      : 'Something went wrong. Please try again.'
    : null;

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!canSubmit || parsed.pct === null) return;
    const pct = parsed.pct;
    add.mutate(
      { email: trimmed, ownershipPct: pct },
      { onSuccess: (result) => setAdded({ email: trimmed, pct, result }) },
    );
  };

  if (added) {
    const total = added.result;
    return (
      <Modal
        title="Owner invited"
        description={setup ? 'Room setup is complete.' : undefined}
        onClose={onClose}
        footer={
          <>
            <span />
            <Button onClick={onClose}>Done</Button>
          </>
        }
      >
        <div className="space-y-2 text-sm" role="status">
          <p>
            <strong>{added.email}</strong> is now one of this room’s owners
            {added.pct !== null ? `, owning ${formatPct(added.pct)}` : ''}. They sign in to the
            seller portal with this email address and can upload their documents from there.
          </p>
          {total && !total.canApprove ? (
            <p className="text-destructive" data-testid="owners-low-warning">
              {total.warning}
            </p>
          ) : null}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Invite an owner"
      description={
        setup
          ? 'Optional last step: add a business owner so they can start sending documents. The AI files everything they upload and matches it to the checklist. You can also do this later on the Access page.'
          : 'Add a business owner so they can start sending documents. The AI files everything they upload and matches it to the checklist.'
      }
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={add.isPending}>
            {setup ? 'Skip for now' : 'Cancel'}
          </Button>
          <Button onClick={() => submit()} disabled={!canSubmit}>
            {add.isPending ? 'Inviting…' : 'Invite owner'}
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={submit} noValidate>
        <div className="space-y-1.5">
          <label htmlFor={emailId} className="text-sm font-medium">
            Owner’s email address
          </label>
          <Input
            id={emailId}
            type="email"
            autoComplete="email"
            placeholder="owner@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={add.isPending}
            aria-describedby={hintId}
            aria-invalid={error ? true : undefined}
          />
          <p id={hintId} className="text-xs text-muted-foreground">
            They become one of this room’s owners. Owners together decide LOIs by an
            ownership-weighted majority of the company; the percentages cannot add up to more
            than 100%.
          </p>
        </div>
        <OwnershipPctField value={pctValue} onChange={setPctText} disabled={add.isPending} />
        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
